import { MessageContentBlock, threadModelIsAuto } from "@hex/common";

import { AgentChatMessageFragment } from "../hex-project-shared/messages/AgentChatMessage.generated";
import { AgentChatThreadStatus } from "../../../generated/graphqlTypes";

import { AgentChatMessage, AgentChatThread } from "./agentChatMessage.types";
import {
  AgentChatThreadFragment,
  AgentChatThreadListItemFragment,
} from "./sharedThreadManagementUtils.generated";

/** Statuses that mean the thread is still processing server-side. */
export const isActiveThreadStatus = (status: AgentChatThreadStatus): boolean =>
  status === "RUNNING" || status === "SAVING_VERSION";

export const messageFragmentToAgentChatMessage = (
  m: AgentChatMessageFragment,
): AgentChatMessage => {
  return {
    id: m.id,
    role: m.role,
    staticId: m.staticId,
    revision: m.revision,
    content: m.content as MessageContentBlock[],
    createdDate: m.createdDate,
    initiatedDate: m.initiatedDate,
    agentChatThreadId: m.agentChatThreadId,
    user: m.user ?? null,
    runId: m.runId,
    debugInfo: m.debugInfo,
    subagentType: m.subagentType ?? null,
    feedback: m.feedback,
  };
};

export const threadFragmentToAgentChatThread = (
  t: AgentChatThreadFragment | AgentChatThreadListItemFragment,
): AgentChatThread => {
  return {
    id: t.id,
    hexId: t.hexId,
    hexVersionId: t.hexVersionId,
    title: t.title,
    createdDate: t.createdDate,
    status: t.status,
    type: t.type,
    autoSaveHexVersionId: t.autoSaveHexVersionId,
    agentMessages: [],
    editsToReview: [],
    userId: t.user?.id ?? null,
    userName: t.user?.name ?? null,
    canPrompt: t.canPrompt,
    auto: "auto" in t ? t.auto : false,
    model: "model" in t ? t.model : null,
    lastUsedEffortLevel:
      "lastUsedEffortLevel" in t ? t.lastUsedEffortLevel : null,
    lastUsedSpeed: "lastUsedSpeed" in t ? t.lastUsedSpeed : null,
    messageCount: "messageCount" in t ? t.messageCount : 0,
    sensitive: t.sensitive,
    taskId: "taskId" in t ? t.taskId : null,
    taskSummary: "taskSummary" in t ? t.taskSummary : null,
    // List-item fragments don't carry `agentMessages`; conservatively
    // assume no truncation until the full thread is loaded.
    hasEarlierMessages:
      "agentMessages" in t
        ? (t.agentMessages.pageInfo?.hasPreviousPage ?? false)
        : false,
    startCursor:
      "agentMessages" in t
        ? (t.agentMessages.pageInfo?.startCursor ?? null)
        : null,
  };
};

export function agentChatThreadToListItemFragment(
  thread: AgentChatThread,
  overrides: Partial<
    Pick<
      AgentChatThreadListItemFragment,
      "auto" | "lastUsedEffortLevel" | "messageCount" | "model" | "status"
    >
  > = {},
): AgentChatThreadListItemFragment {
  return {
    __typename: "AgentChatThread",
    id: thread.id,
    hexId: thread.hexId,
    hexVersionId: thread.hexVersionId,
    title: thread.title,
    createdDate: thread.createdDate,
    status: thread.status,
    autoSaveHexVersionId: thread.autoSaveHexVersionId,
    autoSaveSemanticProjectVersionId: null,
    type: thread.type,
    auto: threadModelIsAuto(thread),
    model: thread.model ?? null,
    lastUsedEffortLevel: thread.lastUsedEffortLevel ?? null,
    lastUsedSpeed: thread.lastUsedSpeed ?? null,
    messageCount: thread.messageCount,
    sensitive: thread.sensitive ?? false,
    canPrompt: thread.canPrompt ?? true,
    taskId: null,
    user:
      thread.userId != null
        ? {
            __typename: "User",
            id: thread.userId,
            name: thread.userName ?? null,
          }
        : null,
    ...overrides,
  };
}
